/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useId, useRef, useState } from 'react';

import invariant from 'tiny-invariant';

import { ThemeProvider } from '@atlaskit/app-provider/theme-provider';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import LottieRenderer from './lottie-renderer';
import { PlayPauseControl } from './play-pause-control';
import VideoRenderer, { type VideoRendererHandle } from './video-renderer';

/**
 * Size preset for the `MotionPlayer` container. Each preset is a combination
 * of a max width and an aspect ratio — the player is fluid up to the preset's
 * max width, then keeps the given ratio. Presets are ordered small → large so
 * `small` never renders wider or taller than `wide`/`large`.
 *
 * - `small`: up to 370px wide, 370×160 ratio — for two-column grids.
 * - `wide`: up to 756px wide, 756×160 ratio — banner-style.
 * - `large`: up to 756px wide, 756×280 ratio — hero/detail.
 * - `hug`: snaps to the asset's natural aspect ratio.
 */
export type MotionPlayerSize = 'small' | 'wide' | 'large' | 'hug';

/**
 * Which renderer drives the player. `lottie` for vector UI motion, `video`
 * for screen recordings or photographic motion.
 */
export type MotionPlayerSource = 'lottie' | 'video';

type MotionPlayerCommonProps = {
	/**
	 * Accessible name for the player (`aria-label`). Describe what the motion
	 * shows, not what the player does.
	 */
	label: string;
	/**
	 * Size preset (max width + aspect ratio) for the player container.
	 * Defaults to `wide`. See {@link MotionPlayerSize}.
	 */
	size?: MotionPlayerSize;
	/**
	 * Renders a 1px `color.border` around the container.
	 */
	hasBorder?: boolean;
	/**
	 * Adds vertical spacing around the player. Defaults to `true`.
	 */
	hasSpacing?: boolean;
	/**
	 * Short caption rendered below the player.
	 */
	caption?: string;
	/**
	 * `data-testid` forwarded to the player container.
	 */
	testId?: string;
	/**
	 * Theme applied to the player chrome so it stays legible against the
	 * asset. `'light'` forces a light surface; `'auto'` follows the page.
	 * Defaults to `'light'` for Lottie and for video without `videoSrcDark`,
	 * `'auto'` for video with `videoSrcDark`. Only re-themes chrome — video
	 * src selection always follows the page theme.
	 */
	assetColorMode?: 'light' | 'auto';
};

type MotionPlayerLottieProps = MotionPlayerCommonProps & {
	source: 'lottie';
	/**
	 * URL of a Lottie JSON file. The animation is fetched on the client rather
	 * than being included in the page bundle.
	 */
	lottieSrc: string;
	/**
	 * Natural Lottie dimensions. Required when `size="hug"` is used.
	 */
	lottieDimensions?: { width: number; height: number };
};

type MotionPlayerVideoProps = MotionPlayerCommonProps & {
	source: 'video';
	/**
	 * Video URL. Use MP4/H.264 for broad browser compatibility.
	 */
	videoSrc: string;
	/**
	 * Dark-mode variant; used when the active color mode is dark.
	 */
	videoSrcDark?: string;
	/**
	 * Poster image shown before the video plays.
	 */
	poster?: string;
};

/**
 * Discriminated by `source` — selects the renderer and required fields.
 */
export type MotionPlayerProps = MotionPlayerLottieProps | MotionPlayerVideoProps;

const styles = cssMap({
	container: {
		position: 'relative',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	large: {
		width: '100%',
		aspectRatio: '756 / 280',
	},
	wide: {
		width: '100%',
		aspectRatio: '756 / 160',
	},
	small: {
		width: '100%',
		aspectRatio: '370 / 160',
	},
	hug: {
		width: '100%',
	},
	/* Per-size max width. Applied to the outer wrapper so the player AND its
	 * caption share the same constrained, centered column. Capping `small` here
	 * guarantees it is always narrower than `wide`/`large`, regardless of how
	 * wide the surrounding container is. */
	largeMaxWidth: {
		maxWidth: '756px',
	},
	wideMaxWidth: {
		maxWidth: '756px',
	},
	smallMaxWidth: {
		maxWidth: '370px',
	},
	/* `hug` snaps to the asset's natural size; no fixed cap. */
	hugMaxWidth: {},
	/* Block-level wrapper that owns the per-size `maxWidth` and centres the whole
	 * unit horizontally. `display: block` (not flex) is required so the player
	 * Box and caption resolve their `width: 100%` against this constrained width
	 * — making the caption exactly the player's width. `width: 100%` prevents
	 * flex parents from shrink-wrapping the wrapper to its min-content width,
	 * which would collapse every size to the smallest preset. */
	sizeWrapper: {
		display: 'block',
		width: '100%',
		marginInline: 'auto',
	},
	/* Gap between the player and its caption (previously the Stack's `space`). */
	captionText: {
		marginBlockStart: token('space.100'),
		textAlign: 'center',
		width: '100%',
	},
	border: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	spacing: {
		marginBlockStart: token('space.400'),
		marginBlockEnd: token('space.400'),
	},
});

/**
 * __MotionPlayer__
 *
 * A docs container for playable motion content (Lottie or HTML5 video) with
 * a shared play/pause overlay. Playback is paused by default so users with
 * `prefers-reduced-motion: reduce` never see motion without opting in.
 *
 * @example
 * ```tsx
 * <MotionPlayer source="lottie" lottieSrc="/animation.json" label="…" />
 * <MotionPlayer source="video" videoSrc="/v.mp4" videoSrcDark="/vd.mp4" label="…" />
 * ```
 */
export const MotionPlayer = (props: MotionPlayerProps): React.JSX.Element => {
	const { label, size = 'wide', hasBorder = false, hasSpacing = true, caption, testId } = props;
	/* Stable id so the caption can be programmatically associated with the
	 * asset via `aria-describedby`. Generated unconditionally (hooks can't be
	 * called conditionally); only wired through when a caption exists. */
	const captionId = useId();

	const [isPlaying, setIsPlaying] = useState(false);
	const [isLottieLoaded, setIsLottieLoaded] = useState(false);
	const videoHandleRef = useRef<VideoRendererHandle | null>(null);

	/* For `size="hug"`, use dimensions provided with static Lottie URLs or
	 * dimensions read from video metadata. */
	const [videoDimensions, setVideoDimensions] = useState<{
		width: number;
		height: number;
	} | null>(null);

	const togglePlay = useCallback(() => {
		/* Compute → fire handle → commit state. Doing side effects inside
		 * the setState updater would double-fire under Strict Mode. */
		const nowPlaying = !isPlaying;
		if (props.source === 'video') {
			if (nowPlaying) {
				videoHandleRef.current?.play();
			} else {
				videoHandleRef.current?.pause();
			}
		}
		setIsPlaying(nowPlaying);
	}, [isPlaying, props.source]);

	const handleLottieLoopComplete = useCallback(() => setIsPlaying(false), []);
	const handleLottieLoadFailed = useCallback(() => setIsPlaying(false), []);
	const handleLottieLoaded = useCallback(() => setIsLottieLoaded(true), []);
	/* `MotionPlayer` only needs to flip `isPlaying` back off when playback
	 * stops, so it deliberately maps both of the renderer's distinct
	 * `onEnded` (natural completion) and `onPaused` (user/browser pause)
	 * callbacks to this one handler. `VideoRenderer` keeps the two events
	 * separate for future consumers that need to tell them apart. */
	const handleVideoStopped = useCallback(() => setIsPlaying(false), []);
	const handleVideoMetadataLoaded = useCallback(
		(dimensions: { width: number; height: number }) => setVideoDimensions(dimensions),
		[],
	);
	if (props.source === 'lottie' && size === 'hug') {
		invariant(
			props.lottieDimensions,
			'`lottieDimensions` is required when `size="hug"` is used with a Lottie source.',
		);
	}

	const effectiveVideoDimensions = props.source === 'video' ? videoDimensions : null;
	const hugDimensions =
		size === 'hug'
			? props.source === 'lottie'
				? props.lottieDimensions
				: effectiveVideoDimensions
			: null;
	const hugAspectRatioStyle = hugDimensions
		? { aspectRatio: `${hugDimensions.width} / ${hugDimensions.height}` }
		: undefined;

	/* The per-size max width lives on the outer wrapper so the player and caption
	 * share the same constrained, centered column. It is referenced via
	 * `styles[maxWidthKey]` (direct member access on the module-scope `cssMap`)
	 * so it satisfies `consistent-css-prop-usage`. */
	const maxWidthKey = `${size}MaxWidth` as const;

	/* Default: Lottie + dark-less video are light-only; only video with
	 * `videoSrcDark` is theme-adaptive. See `assetColorMode` JSDoc. */
	const heuristicAssetColorMode: 'light' | 'auto' =
		props.source === 'lottie' || (props.source === 'video' && !props.videoSrcDark)
			? 'light'
			: 'auto';
	const effectiveAssetColorMode = props.assetColorMode ?? heuristicAssetColorMode;

	/* Associate the caption with the element that actually carries the
	 * accessible name (the `<video>` / Lottie `role="img"`), not the plain
	 * wrapper `<Box>` — `aria-describedby` is only reliably announced on an
	 * element that has a role/name. */
	const describedById = caption ? captionId : undefined;

	const renderer =
		props.source === 'lottie' ? (
			<LottieRenderer
				lottieSrc={props.lottieSrc}
				isPlaying={isPlaying}
				label={label}
				describedById={describedById}
				onLoopComplete={handleLottieLoopComplete}
				onLoaded={handleLottieLoaded}
				onLoadFailed={handleLottieLoadFailed}
			/>
		) : (
			<VideoRenderer
				ref={videoHandleRef}
				videoSrc={props.videoSrc}
				videoSrcDark={props.videoSrcDark}
				poster={props.poster}
				label={label}
				describedById={describedById}
				onEnded={handleVideoStopped}
				onPaused={handleVideoStopped}
				onMetadataLoaded={handleVideoMetadataLoaded}
			/>
		);

	const playerBox = (
		<Box
			testId={testId}
			xcss={cx(styles.container, styles[size], hasBorder && styles.border)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- The hug aspect ratio is derived from the runtime asset dimensions and cannot be expressed in a static cssMap entry.
			style={hugAspectRatioStyle}
		>
			{renderer}
			<PlayPauseControl
				isPlaying={isPlaying}
				onToggle={togglePlay}
				isDisabled={props.source === 'lottie' && !isLottieLoaded}
			/>
		</Box>
	);

	/* Wrap the Box (not just the renderer) so background/border tokens also
	 * resolve against the forced-light theme. The caption stays outside and
	 * follows the page theme. Video src selection is unaffected. */
	const player =
		effectiveAssetColorMode === 'light' ? (
			<ThemeProvider defaultColorMode="light">{playerBox}</ThemeProvider>
		) : (
			playerBox
		);

	/* A block-level wrapper owns the per-size max width and horizontal centring.
	 * Because it is `display: block` (not flex), its children resolve their
	 * `width: 100%` against the wrapper's constrained width — so the player and
	 * caption are both exactly the player's width, and the caption (centred
	 * text) sits centred directly under the player. Using a flex wrapper here
	 * would shrink-wrap the aspect-ratio player Box to its minimum, collapsing
	 * every size to "small". */
	return (
		<Box xcss={cx(styles.sizeWrapper, styles[maxWidthKey], hasSpacing && styles.spacing)}>
			{player}
			{caption ? (
				<Box xcss={styles.captionText}>
					<Text id={captionId} size="small" align="center" color="color.text.subtlest">
						{caption}
					</Text>
				</Box>
			) : null}
		</Box>
	);
};
